import { Application } from 'pixi.js';

type PointerCallback = (event: PointerEvent) => void;

type KeyboardCallback = (event: KeyboardEvent) => void;

type WheelCallback = (event: WheelEvent) => void;

export interface InputBindings {
  onPointerDown?: PointerCallback;
  onPointerMove?: PointerCallback;
  onPointerUp?: PointerCallback;
  onPointerCancel?: PointerCallback;
  onKeyDown?: KeyboardCallback;
  onKeyUp?: KeyboardCallback;
  onWheel?: WheelCallback;
}

export class InputManager {
  private static instance: InputManager | null = null;

  private bindings: InputBindings | null = null;
  private view: HTMLCanvasElement | null = null;
  private isWheelEventActive = false;

  private constructor() {}

  public static initialize(app: Application): InputManager {
    if (!InputManager.instance) {
      InputManager.instance = new InputManager();
      InputManager.instance.attachToCanvas(app.view as HTMLCanvasElement);
    }

    return InputManager.instance;
  }

  public static getInstance(): InputManager {
    if (!InputManager.instance) {
      throw new Error('InputManager accessed before initialization');
    }

    return InputManager.instance;
  }

  public registerBindings(bindings: InputBindings): void {
    this.bindings = bindings;
  }

  private attachToCanvas(canvas: HTMLCanvasElement): void {
    this.view = canvas;

    canvas.addEventListener('pointerdown', this.handlePointerDown);
    canvas.addEventListener('pointermove', this.handlePointerMove);
    canvas.addEventListener('pointerup', this.handlePointerUp);
    canvas.addEventListener('pointercancel', this.handlePointerCancel);
    canvas.addEventListener('wheel', this.handleWheel, { passive: false });

    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
  }

  private handlePointerDown = (event: PointerEvent): void => {
    this.bindings?.onPointerDown?.(event);
  };

  private handlePointerMove = (event: PointerEvent): void => {
    // Skip pointer move events during wheel operations to prevent disconnection issues
    if (this.isWheelEventActive) {
      return;
    }
    this.bindings?.onPointerMove?.(event);
  };

  private handlePointerUp = (event: PointerEvent): void => {
    this.bindings?.onPointerUp?.(event);
  };

  private handlePointerCancel = (event: PointerEvent): void => {
    this.bindings?.onPointerCancel?.(event);
  };

  private handleKeyDown = (event: KeyboardEvent): void => {
    this.bindings?.onKeyDown?.(event);
  };

  private handleKeyUp = (event: KeyboardEvent): void => {
    this.bindings?.onKeyUp?.(event);
  };

  private handleWheel = (event: WheelEvent): void => {
    event.preventDefault();
    
    // Set flag to temporarily suppress pointer events
    this.isWheelEventActive = true;
    
    try {
      this.bindings?.onWheel?.(event);
    } finally {
      // Clear flag after a short delay to allow zoom operations to complete
      setTimeout(() => {
        this.isWheelEventActive = false;
      }, 10);
    }
  };

  public dispose(): void {
    if (!this.view) {
      return;
    }

    this.view.removeEventListener('pointerdown', this.handlePointerDown);
    this.view.removeEventListener('pointermove', this.handlePointerMove);
    this.view.removeEventListener('pointerup', this.handlePointerUp);
    this.view.removeEventListener('pointercancel', this.handlePointerCancel);
    this.view.removeEventListener('wheel', this.handleWheel);

    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);

    this.bindings = null;
    this.view = null;
  }
}
